/**
 * توكنز التصميم — **المصدر الوحيد لأي لون أو مسافة في المنتج**.
 *
 * المشتري يختار لوناً واحداً في معالج الإعداد، فيتغيّر النظام كلّه. وهذا لا
 * يعمل إلا إذا كان اللون **مشتقّاً** لا مكتوباً: درجةٌ أفتح للتحويم، وخلفيةٌ
 * باهتة للعنصر النشط، وحدٌّ فاصل — كلها محسوبة من نفس اللون هنا.
 *
 * **والأخضر والأصفر والأحمر ليست من اللون المختار.** هي محجوزة للحالات، ولو
 * اشتُقّت من هوية المشتري لصار «ناجح» أزرقَ عند أحدهم وأحمرَ عند آخر — فيفقد
 * اللون معناه، وهو آخر ما تحتاجه شاشةٌ يقرؤها موظف ثماني ساعات.
 */
/** لون النظام الافتراضي، ونفسه في سجل الإعدادات `identity.accentColor` */
export const DEFAULT_ACCENT = "#0B6E75";
/**
 * **ألوانُ هويةٍ مقترحة لمعالج الإعداد** — والأوّلُ هو الافتراضيّ.
 *
 * هنا لا في الشاشة: **فلونٌ مكتوبٌ في شاشةٍ يُسقط البناء**، والتوكنز موضعُ الألوان
 * الوحيد. والمدقّقُ في السيرفر هو الحكم على ما يُحفَظ.
 */
export const ACCENT_PRESETS = [
    DEFAULT_ACCENT,
    "#1C5A8A",
    "#5B3FA8",
    "#8A4B2A",
    "#B42357",
    "#1B7A46",
];
/**
 * **واسمُ كلّ لونٍ مقترح** — فالدائرةُ تُقرأ «تركواز» لا «#0B6E75».
 *
 * وكانت كلُّ دائرةٍ تُسمّى برقمها للقارئ الصوتي وفي التلميح: رقمٌ ستّ عشريٌّ في
 * وجه صاحب وكالة. **ولونٌ محفوظٌ من خارجها يُقال «لون مخصّص»** — لا رقمُه.
 */
const ACCENT_NAME = {
    "#0B6E75": "colors.teal",
    "#1C5A8A": "colors.blue",
    "#5B3FA8": "colors.purple",
    "#8A4B2A": "colors.brown",
    "#B42357": "colors.raspberry",
    "#1B7A46": "colors.green",
};
export function accentNameKey(hex) {
    const found = ACCENT_PRESETS.find((preset) => preset.toLowerCase() === hex.toLowerCase());
    return found === undefined ? "colors.custom" : ACCENT_NAME[found];
}
/**
 * يحوّل اللون الذي اختاره المشتري إلى مكوّناته.
 *
 * يقبل `#rgb` و`#rrggbb`. وأي شيء آخر **يرجع الافتراضي بدل أن ينفجر**: لونٌ
 * خاطئ في إعداد لا يجوز أن يمنع النظام من الإقلاع.
 */
export function parseAccent(value) {
    const hex = value.trim().replace(/^#/, "");
    const full = hex.length === 3
        ? [...hex].map((c) => c + c).join("")
        : hex.length === 6
            ? hex
            : DEFAULT_ACCENT.slice(1);
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b))
        return parseAccent(DEFAULT_ACCENT);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    const h = d === 0
        ? 0
        : max === r
            ? 60 * (((g - b) / d) % 6)
            : max === g
                ? 60 * ((b - r) / d + 2)
                : 60 * ((r - g) / d + 4);
    return {
        h: Math.round((h + 360) % 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100),
    };
}
/** ألوان الحالات — ثابتة في الوضعين، ولا تُشتقّ من هوية أحد */
const STATUS = {
    light: {
        ok: "#1B7A46",
        "ok-bg": "#E6F3EB",
        warn: "#9A6408",
        "warn-bg": "#FBF1DF",
        dang: "#A32A1E",
        "dang-bg": "#FAE9E7",
        info: "#1C5A8A",
        "info-bg": "#E7F0F7",
    },
    dark: {
        ok: "#4FBF80",
        "ok-bg": "#12301F",
        warn: "#D9A43C",
        "warn-bg": "#31260F",
        dang: "#E8705F",
        "dang-bg": "#331612",
        info: "#5FA5D8",
        "info-bg": "#122633",
    },
};
const SURFACES = {
    light: {
        bg: "#F4F6F5",
        surf: "#FFFFFF",
        "surf-2": "#EDF0EE",
        ink: "#131817",
        "ink-2": "#4B5451",
        "ink-3": "#818B87",
        line: "#DFE4E1",
        "line-2": "#EBEEEC",
        sh: "0 1px 2px rgba(19,24,23,.05)",
        "sh-2": "0 4px 14px -6px rgba(19,24,23,.18),0 1px 3px rgba(19,24,23,.06)",
        "sh-3": "0 18px 46px -18px rgba(19,24,23,.35)",
    },
    dark: {
        bg: "#0E1312",
        surf: "#161C1A",
        "surf-2": "#1E2523",
        ink: "#E9EDEB",
        "ink-2": "#A6AFAB",
        "ink-3": "#7A8480",
        line: "#2A322F",
        "line-2": "#212826",
        sh: "0 1px 2px rgba(0,0,0,.4)",
        "sh-2": "0 4px 14px -6px rgba(0,0,0,.6)",
        "sh-3": "0 18px 46px -18px rgba(0,0,0,.9)",
    },
};
/** ثوابت الشكل — لا تتغيّر بالوضع ولا بالمشتري. الكثافة قرار معتمَد. */
const FIXED = {
    s1: "4px",
    s2: "8px",
    s3: "12px",
    s4: "16px",
    s5: "24px",
    s6: "32px",
    s7: "48px",
    r: "8px",
    "r-lg": "12px",
    "m-fast": "180ms",
    "m-base": "250ms",
    "m-slow": "450ms",
    ease: "cubic-bezier(.16,1,.3,1)",
    f: '"IBM Plex Sans Arabic","Segoe UI",system-ui,sans-serif',
    /** **خطُّ العناوين** — Readex Pro، وإن لم يُحمَّل بعدُ فخطُّ النصّ مكانه */
    fh: '"Readex Pro","IBM Plex Sans Arabic","Segoe UI",system-ui,sans-serif',
    fm: '"IBM Plex Mono",ui-monospace,monospace',
    "nav-h": "60px",
    "text-table": "13px",
    "text-form": "14px",
};
/**
 * مجموعة المتغيّرات كاملةً لوضعٍ واحد.
 *
 * تُطبَّق على الجذر مرة واحدة، فتتغيّر الواجهة كلها. لا يقرأ أحدٌ لوناً من
 * مكانٍ آخر — قاعدة lint تكسر البناء عند أي لون مكتوب في CSS خارج هنا.
 */
export function themeVariables(accent, mode) {
    const l = mode === "dark" ? Math.max(accent.l, 46) : accent.l;
    return {
        "--acc-h": String(accent.h),
        "--acc-s": `${accent.s}%`,
        "--acc-l": `${l}%`,
        "--accent": `hsl(${accent.h} ${accent.s}% ${l}%)`,
        "--accent-hi": `hsl(${accent.h} ${accent.s}% ${Math.min(l + 9, 96)}%)`,
        "--accent-soft": mode === "dark" ? `hsl(${accent.h} 40% 15%)` : `hsl(${accent.h} 45% 94%)`,
        "--accent-line": mode === "dark" ? `hsl(${accent.h} 35% 28%)` : `hsl(${accent.h} 40% 82%)`,
        "--on-accent": "#fff",
        ...Object.fromEntries(Object.entries(SURFACES[mode]).map(([k, v]) => [`--${k}`, v])),
        ...Object.fromEntries(Object.entries(STATUS[mode]).map(([k, v]) => [`--${k}`, v])),
        ...Object.fromEntries(Object.entries(FIXED).map(([k, v]) => [`--${k}`, v])),
    };
}
/** أسماء كل المتغيّرات — يستعملها الاختبار ليمنع نقصان وضعٍ عن الآخر */
export const TOKEN_NAMES = Object.keys(themeVariables({ h: 187, s: 81, l: 25 }, "light"));
//# sourceMappingURL=tokens.js.map